import { type SelectList, SelectListEvent } from '@acies/core';
import { InputField, InputFieldEvent, PopoverContainer, Spinner } from '@acies/ui';
import { Effect, FiberHandle, Scope } from 'effect';
import { type ApiServices, provideCallServices } from '../../../../services/runtime';
import SearchAutoCompleteEvent from './search-auto-complete-event';
import SearchItemRenderer from './search-item-renderer';
import SearchList from './search-list';
import type ISearchResult from './search-result';

/**
 * How a SearchAutoComplete asks for what matches the text so far.
 *
 * Succeeding is an answer: these matched, and nothing outside them did. Failing is the search not having been made at all — no account, a refused token, an unreachable API, an answer that did not match the schema — which is a different thing from nothing matching, and the component shows it differently. What it failed
 * with is not the component's business, so the error is anything at all.
 *
 * An Effect rather than a promise, so the component can interrupt a search a later keystroke has overtaken: the request out for it is aborted, and nothing it would have answered reaches the screen. It runs over the Configurator's services — see `provideCallServices`.
 *
 * Whoever sets it is what decides what is searched, and that is the whole of what makes this component general. It resolves its own endpoint: the construction model search builds a store path from the account, and a lookup search is handed the path the engine already wrote into the configuration. Neither is the
 * component's business — and a property holding an endpoint instead of a function would make the first one impossible, since that path is only knowable through an account lookup.
 */
export type Search<Item extends ISearchResult> = (limit: number, query: string) => Effect.Effect<ReadonlyArray<Item>, unknown, ApiServices>;

/** How many results a search asks for by default, so the popover stays a shortlist to pick from. */
const defaultSearchLimit = 6;

/** The room at the end of the field while nothing is being searched for: the same as at its start, so the field reads like any other in the row. */
const paddingWithoutSpinner = 10;

/** The room at the end of the field while a search is out: the spinner's 16 and its 10 from the edge, and the 10 the text keeps from it. */
const paddingWithSpinner = 36;

/**
 * An InputField that searches as it is typed into, and offers what it found in a popover to pick from.
 *
 * What is searched is not decided here: {@link SearchAutoComplete.search} is set by whoever puts one on screen, and everything this component knows about a result is {@link ISearchResult} — a code and a name. So one component serves the construction model picker on a new position and the value picker on a lookup
 * parameter, which are the same interaction against different collections.
 *
 * The popover is a shortlist and not a catalogue. The collections behind it run to thousands of entries, so the API is asked for the first {@link SearchAutoComplete.searchLimit} matches on every keystroke, and nothing is filtered here.
 */
export default class SearchAutoComplete<Item extends ISearchResult> extends PopoverContainer<Item> {
	/** The value the field is meant to be holding: what it was seeded with, or what the last change settled on. What a change is measured against. */
	#committedText: string;

	/**
	 * Whether the user has typed in the field since it was focused.
	 *
	 * What tells a field the user is answering in from one they are merely standing in. The two are left alone differently when the field is seeded again — see {@link SearchAutoComplete.seed} — and say different things when the user leaves: typed, what the field holds is theirs; typed nothing, nothing is said, whatever
	 * the field shows. See #focusOut.
	 */
	#typing: boolean;
	#hasKeyboardListener: boolean;
	#inputField!: InputField;
	#search: Search<Item> | null;

	/**
	 * The search that is out, if one is.
	 *
	 * A FiberHandle holds one fiber and interrupts it when the next one is run in it, which is exactly what a search overtaken by a keystroke is: two searches can come back in either order, and the older answer must not be the one left on screen. Clearing it is how searching stops altogether rather than being
	 * overtaken — see #giveUpOnSearches.
	 */
	#searching: FiberHandle.FiberHandle<void, never>;
	#searchLimit: number;
	#spinner!: Spinner;
	public constructor() {
		super();
		this.alignment = 'right';
		// PopoverContainer asks for 240, which is wider than the columns these sit in: the key the
		// InputField shows is short, and only the popover needs the room
		this.minWidth = 128;
		// exactly the six results a search asks for, so a full list neither scrolls nor cuts one off
		this.popoverMaxHeight = 228;
		// wider than the anchor, since a name is far longer than the key the InputField holds
		this.popoverWidth = 640;
		this.#hasKeyboardListener = false;
		this.#search = null;
		this.#committedText = '';
		this.#typing = false;
		// in a scope nothing closes, since a component taken out of the document may be put back in:
		// a search out when it goes is given up on instead — see disconnected
		this.#searching = Effect.runSync(Scope.provide(FiberHandle.make<void, never>(), Scope.makeUnsafe()));
		this.#searchLimit = defaultSearchLimit;
		// we bind the arrowDownListener, since we are listening in the window scope.
		// we can't bind #private methods, so we do it typesafe instead.
		this.arrowDownListener = this.arrowDownListener.bind(this);
	}

	/**
	 * The icon shown inside the field, or null for none.
	 *
	 * One of the two properties passed through to the InputField this holds, the way `AutoComplete` passes the same two through to its own — see {@link SearchAutoComplete.placeholder}.
	 *
	 * Typed off the field rather than against the icon list, because that list is not exported from `@acies/ui`. It comes to the same type, and it cannot fall out of step with what the field accepts.
	 */
	public set icon(value: InputField['icon']) {
		this.inputField.icon = value;
	}

	public get icon(): InputField['icon'] {
		return this.inputField.icon;
	}

	/**
	 * Whether the field is shown in the error state.
	 *
	 * The third property passed through to the InputField, beside `icon` and `placeholder`, and for the same reason: a caller with something to say about the value should not have to reach through to the field to say it. The component sets it itself when a search could not be made, and clears it when one could — so an
	 * error set from outside is cleared by the next search that answers, which is when the user has typed again and the old refusal no longer describes what the field holds.
	 */
	public set error(value: boolean) {
		this.inputField.error = value;
	}

	public get error(): boolean {
		return this.inputField.error;
	}

	public get inputField(): InputField {
		if (!this.#inputField) {
			this.#inputField = new InputField();
			// the engine writes its codes in capitals, whichever collection they come from, so the
			// user does not hold shift for one
			this.#inputField.autoCapitalize = true;
			// the same room at the end as at the start, until a search is out: the spinner then lies
			// over the end of the field, and the padding grows to keep the text clear of it — see
			// #startSpinner. The text is set to the left, so what moves is its end, not where it reads from
			this.#inputField.paddingRight = paddingWithoutSpinner;
			this.#inputField.width = 'fill';
		}
		return this.#inputField;
	}

	/**
	 * The hint shown in the field while it is empty, saying what belongs in it.
	 *
	 * Passed through to the InputField this holds, so a caller never has to reach through a child to say what its own field should read. `AutoComplete` in `@acies/ui` passes `placeholder` and `icon` through for the same reason, and this is the same pair.
	 *
	 * A property and no matching attribute, unlike `AutoComplete`: nothing in the Configurator is built from markup, so an observed attribute would be machinery for a way of using this that does not exist here.
	 */
	public set placeholder(value: string) {
		this.inputField.placeholder = value;
	}

	public get placeholder(): string {
		return this.inputField.placeholder;
	}

	/**
	 * What the component asks as the user types, or null while nothing has been set.
	 *
	 * Null is a component that searches nothing: the field is typed into, no call is made, and the popover stays closed. It is where a component starts, before it is wired, and what a caller sets when what is typed has nowhere to be looked up — the create row's key while it is a text position, say.
	 */
	public set search(value: Search<Item> | null) {
		assertIsValidSearch(value);
		this.#search = value;
	}

	public get search(): Search<Item> | null {
		return this.#search;
	}

	/**
	 * The most results a search asks for.
	 *
	 * Six by default, which is what {@link PopoverContainer.popoverMaxHeight} is set here to hold: raise one without the other and a full list either scrolls or is cut off. Both are defaults a caller overrides together.
	 */
	public set searchLimit(value: number) {
		assertIsValidSearchLimit(value);
		this.#searchLimit = value;
	}

	public get searchLimit(): number {
		return this.#searchLimit;
	}

	/**
	 * Selects an item.
	 *
	 * The list stays silent about it: a SearchAutoCompleteEvent is only dispatched for a selection the user made.
	 *
	 * Null clears the selection, whether it was made through this setter or by the user clicking: the list measures it against what is actually selected. {@link reset} goes further, and empties the list and the field as well.
	 */
	public set selectedItem(value: Item | null) {
		this.listChild.selectedItem = value;
	}

	/**
	 * The item the user picked in the popover, or null while none is picked.
	 *
	 * Held by the list rather than here, so it answers for what is actually selected: the list clears its selection when a new search replaces the data, which is exactly when an item picked from the previous answer stops meaning anything.
	 */
	public get selectedItem(): Item | null {
		return this.listChild.selectedItem;
	}

	/**
	 * Shows that a search is out, over the right hand end of the field being typed into.
	 *
	 * Hidden until there is a search to wait for, so the field looks the same as any other in the row until the user starts typing.
	 */
	public get spinner(): Spinner {
		if (!this.#spinner) {
			this.#spinner = new Spinner();
			this.#spinner.height = 16;
			// lifted out of the row and laid over the end of the field, so the field keeps the whole
			// width and the row is laid out the same whether the spinner is up or not; the field makes
			// room for it in its own padding meanwhile — see #startSpinner
			this.#spinner.position = 'absolute';
			this.#spinner.right = 10;
			this.#spinner.visible = false;
			this.#spinner.width = 16;
		}
		return this.#spinner;
	}

	/**
	 * Puts a value in the field without it counting as a change the user made.
	 *
	 * For seeding a field with what it is already showing — the key a lookup parameter holds, say. Nothing is dispatched, no search goes out, and the value becomes the one a later change is measured against, so seeding a field and tabbing straight through it says nothing. Setting it is what a caller should do instead
	 * of reaching through to the InputField, which leaves the component with no idea what it is supposed to be holding.
	 */
	public set text(value: string) {
		this.inputField.text = value;
		this.#committedText = value;
	}

	/**
	 * What the user has typed, whether or not they picked one of the results it found.
	 *
	 * Picking one puts its key here, so this is the key either way — and it is the only one left when someone types a key they already know and tabs straight on without opening the popover.
	 */
	public get text(): string {
		return this.inputField.text;
	}

	/**
	 * Empties the field and forgets both what was found and what was picked.
	 *
	 * Handing the list new data is what clears the pick reliably, however it was made: the list drops its whole selection when its data is replaced, where the `selectedItem` setter only reaches a pick it made itself. The previous answer is dropped with it, since it answered a search that is over — as is one still out,
	 * which would otherwise fill the emptied field's list in behind it.
	 */
	public reset(): void {
		this.#giveUpOnSearches();
		this.data = [];
		// through the setter, so an emptied field is also what a later change is measured against:
		// a row put back to empty and then tabbed through has not been changed by anyone
		this.text = '';
		this.#typing = false;
	}

	/**
	 * Moves focus to the InputField the search is typed into.
	 *
	 * The component itself is a PopoverContainer and holds no focus, so a caller asking it to take focus means the field they type a key into.
	 */
	public override focus(options?: FocusOptions): void {
		this.inputField.focus(options);
	}

	protected override createListChild(): SelectList<Item> {
		return new SearchList<Item>();
	}

	protected override disconnected(): void {
		super.disconnected();
		// a search out has nothing left to answer into
		this.#giveUpOnSearches();
		// the ArrowDown listener lives on the window, so being removed while the InputField is
		// focused must not leak it. Chromium blurs a focused field taken out of the DOM, and that
		// blur removes the listener — measured rather than assumed — but not every browser does, so
		// it is removed here as well rather than left to the blur
		if (this.#hasKeyboardListener) {
			this.#removeArrowDownListener();
		}
	}

	protected override initialize(): void {
		// the InputField and the spinner are added before the popover child, so they render as the
		// anchor it is positioned against
		this.addComponents([this.inputField, this.spinner]);
		super.initialize();
		this.#addEventListeners();
	}

	/** The ArrowDown listener is on the window, so it is only attached while the InputField is focused, and removed again on blur, so we don't leak it. */
	#addArrowDownListener(): void {
		if (!this.#hasKeyboardListener) {
			this.#hasKeyboardListener = true;
			window.addEventListener('keyup', this.arrowDownListener);
		}
	}

	#addEventListeners(): void {
		// on this component rather than the window, so it needs no adding and removing as focus
		// comes and goes: a keystroke anywhere inside the component reaches it either way
		this.addEventListener('keydown', this.#keyDown as EventListener);
		// focusout rather than the InputField's own blur, because the two are not the same question.
		// The field is blurred on the way to picking an item — the pointer going to the popover blurs
		// it, and so does an arrow key walking into the list — and neither of those is the user
		// leaving. focusout carries where the focus went, so this only fires when it went somewhere
		// outside the component altogether
		this.addEventListener('focusout', this.#focusOut as EventListener);
		this.addEventListener(InputFieldEvent.BLURRED, this.#eventListener as EventListener);
		this.addEventListener(InputFieldEvent.CHANGED, this.#eventListener as EventListener);
		this.addEventListener(InputFieldEvent.FOCUSED, this.#eventListener as EventListener);
		this.addEventListener(SelectListEvent.SELECTION_CHANGED, this.#listItemChanged as EventListener);
	}

	/**
	 * Puts the value the field is meant to hold, without taking a field the user is in out of their hands.
	 *
	 * For a row handed the same parameter again, made anew by the API, where the value may have moved while the user was typing into this very field — see AnswerItemRenderer. The value becomes what a change is measured against, the way {@link SearchAutoComplete.text} makes it; the field itself is only written when the
	 * user is neither in it nor has typed in it. Left alone, what they typed stays theirs, and is said when they leave; typed nothing, the field shows this value when they leave — see #focusOut.
	 */
	public seed(value: string): void {
		this.#committedText = value;
		if (!this.#typing && !this.contains(document.activeElement)) {
			this.inputField.text = value;
		}
	}

	#blurred(): void {
		// the field has been left, so a search still out is answering a key nobody is typing any
		// more: it would fill the popover in behind the user, and replacing the data would take the
		// selection with it. The popover itself is left as it is — an item is clicked through a
		// blur, so what is on screen must stay where the pointer is going
		this.#giveUpOnSearches();
		this.#removeArrowDownListener();
		// the InputField's own blur stops here, so this is what tells a listener the field is done
		// being typed in — whether or not anything was picked from the popover
		this.dispatchEvent(new SearchAutoCompleteEvent<Item>(SearchAutoCompleteEvent.BLURRED, this.text));
	}

	#changed(): void {
		this.#typing = true;
		// what to show is asked of the API, so nothing is filtered here
		if (this.inputField.text.length > 0) {
			// forked, since the event listener this came from is synchronous: the popover opens on the
			// keystroke and fills in once the API has answered
			this.#runSearch();
			if (this.state === 'closed') {
				this.show();
			}
		} else {
			// emptying the field is the end of searching, not a pause in it: a search still out
			// would otherwise answer into an empty field and be the list the next keystroke opens
			// onto
			this.#giveUpOnSearches();
			this.data = [];
			this.hide();
		}
	}

	/** Only BLURRED, CHANGED and FOCUSED are listened for, so anything else is a blur. */
	#eventListener(event: InputFieldEvent): void {
		// prevent the default events to bubble up the DOM
		event.stopImmediatePropagation();
		if (event.type === InputFieldEvent.FOCUSED) {
			this.#focused();
		} else if (event.type === InputFieldEvent.CHANGED) {
			this.#changed();
		} else {
			this.#blurred();
		}
	}

	#focused(): void {
		// a field just focused has not been typed in yet, whatever happened the last time it was
		this.#typing = false;
		this.#addArrowDownListener();
	}

	/**
	 * Announces a key the user typed and then left, which is the half of changing a value that picking from the popover does not cover.
	 *
	 * Only when the focus has gone outside the component: `relatedTarget` is the element it went to, and on the way to a pick that is the item renderer and then the InputField again, both of them inside. A null means it went nowhere this document knows about — tabbed past the end, or the window itself left — and that
	 * counts as leaving.
	 *
	 * Silent unless the value actually moved. Tabbing through a field, or typing something and putting it back the way it was, is not a change, and a listener that prices every answer it is told about should not be told about those.
	 *
	 * Nothing is dispatched for a pick, because the pick already said so and set {@link SearchAutoComplete.text} to the key on its way through — so by the time the focus leaves, there is nothing left to report.
	 */
	#focusOut(event: FocusEvent): void {
		const to = event.relatedTarget;
		if (to instanceof Node && this.contains(to)) {
			return;
		}
		const typing = this.#typing;
		this.#typing = false;
		if (this.text === this.#committedText) {
			return;
		}
		// nothing typed is nothing said, whatever the field shows: the field was seeded with another
		// value while the user stood in it — see seed() — and that value is shown now that they have
		// left, rather than the old one being sent back as their answer
		if (!typing) {
			this.inputField.text = this.#committedText;
			return;
		}
		this.#committedText = this.text;
		// null, because nothing was picked: what the user typed is a key, and turning a key back into
		// the thing it names is a question for whoever holds the collection it came from
		this.dispatchEvent(new SearchAutoCompleteEvent<Item>(SearchAutoCompleteEvent.VALUE_CHANGED, this.text));
	}

	#focusFirstItemRenderer(): void {
		const firstElement = this.listChild.children.item(0);
		// the list is empty until the API has answered, so there is not always an item to focus
		if (firstElement instanceof SearchItemRenderer) {
			firstElement.focus();
		}
	}

	/**
	 * Gives up on the search that is still out, so nothing it answers reaches the screen.
	 *
	 * Interrupted rather than left to land, so the request out for it is aborted too. Nothing is left to wait for, so the spinner comes down with it.
	 */
	#giveUpOnSearches(): void {
		Effect.runFork(FiberHandle.clear(this.#searching));
		this.#stopSpinner();
	}

	/**
	 * Closes the popover when the user tabs on, or presses Enter in the field, leaving an open list behind nothing.
	 *
	 * Enter lets go of the field the way Tab does — the InputField blurs once it is released — so it is the user done typing either way. Only Enter pressed in the field, though: Enter on an item is how the keyboard picks it, and the pick is made as the key comes back up, so closing on the way down would hide the item
	 * before it could be picked. The pick closes the popover itself.
	 *
	 * On keydown rather than keyup, since the key that moves focus also blurs the InputField, and a listener that waits for the key to come back up would be gone by then.
	 *
	 * Blur would be the obvious moment and is the wrong one: clicking an item blurs the InputField before the click completes, so closing there takes the item out from under the pointer.
	 */
	#keyDown(event: KeyboardEvent): void {
		const isEnter = event.code === 'Enter' || event.code === 'NumpadEnter';
		if (event.code === 'Tab' || (isEnter && event.target instanceof Node && this.inputField.contains(event.target))) {
			this.hide();
		}
	}

	#listItemChanged(event: SelectListEvent<Item>): void {
		// the SelectList also reports a null selectedItem when the data is replaced
		if (event.selectedItem) {
			// the key is what the picked thing is referred to by, so that is what the field shows.
			// Through the setter, so the pick becomes the value the field is now meant to be holding
			// and the focusout that follows does not read it as a second, typed change
			this.text = event.selectedItem.key;
			this.hide();
			this.dispatchEvent(new SearchAutoCompleteEvent<Item>(SearchAutoCompleteEvent.VALUE_CHANGED, this.text, event.selectedItem));
		}
	}

	#removeArrowDownListener(): void {
		this.#hasKeyboardListener = false;
		window.removeEventListener('keyup', this.arrowDownListener);
	}

	/**
	 * Asks {@link SearchAutoComplete.search} for what matches what the user has typed so far.
	 *
	 * A failure means the search could not be made at all, so there is nothing to show and the list is left as it was. An empty answer is a real one and does replace it: it means the API looked and nothing matched.
	 *
	 * Run in #searching, so the search before it is interrupted: it is answering what the user typed before, and neither its answer nor its end may reach the screen — the spinner would stop while this one was still out.
	 */
	#runSearch(): void {
		const { search } = this;
		// nothing has been wired up, so there is nothing to ask and nothing to wait for: the
		// spinner is never raised, rather than raised and left spinning over a call nobody made
		if (search === null) {
			return;
		}
		this.#startSpinner();
		const searched = search(this.searchLimit, this.inputField.text).pipe(
			Effect.matchEffect({
				// the field says the search could not be made, rather than the picker going quiet
				// and looking like nothing matched
				onFailure: () =>
					Effect.sync(() => {
						this.#stopSpinner();
						this.inputField.error = true;
					}),
				onSuccess: (result) =>
					Effect.sync(() => {
						this.#stopSpinner();
						this.inputField.error = false;
						this.data = [...result];
					}),
			}),
		);
		Effect.runFork(FiberHandle.run(this.#searching, provideCallServices(searched)));
	}

	/**
	 * Puts the spinner up over the end of the field, and moves the end of the text out from under it.
	 *
	 * The padding rather than a permanent reservation, so the field reads like any other in the row while nothing is being searched for. The text is set to the left, so growing the padding moves only its end — and only while the spinner is there to be kept clear of.
	 */
	#startSpinner(): void {
		this.spinner.visible = true;
		this.spinner.spin = true;
		this.inputField.paddingRight = paddingWithSpinner;
	}

	/** Takes the spinner down, and gives the text its end of the field back: the search has answered, or it has been given up on. */
	#stopSpinner(): void {
		this.spinner.spin = false;
		this.spinner.visible = false;
		this.inputField.paddingRight = paddingWithoutSpinner;
	}

	private arrowDownListener(event: KeyboardEvent): void {
		if (event.code === 'ArrowDown') {
			this.#focusFirstItemRenderer();
		}
	}
}
customElements.define('cf-search-auto-complete', SearchAutoComplete);

function assertIsValidSearch(value: unknown): asserts value is Search<ISearchResult> | null {
	if (value === null || typeof value === 'function') {
		return;
	}
	throw new TypeError(`[${String(value)}] is invalid, must be a function or null`);
}

function assertIsValidSearchLimit(value: unknown): asserts value is number {
	if (typeof value === 'number' && Number.isInteger(value) && value > 0) {
		return;
	}
	throw new RangeError(`[${String(value)}] is invalid, must be a positive integer`);
}
